/**
 * Centralized logging service for the therapist app.
 *
 * Public API preserved verbatim so the ~185 existing call sites don't
 * need to change. Under the hood, all DB writes now flow through
 * `@repo/observability/client` — that's what gets us traceId,
 * sessionId, breadcrumbs, requestBody, feature tags on every row that
 * used to come out of this service.
 *
 * `clientLogger` from `@repo/observability/client` is a browser-only
 * singleton (no-ops on the server). This service was always effectively
 * client-only anyway — it imported the browser supabase client. Server
 * routes use `logServerError` / `withObservability` via
 * `@/lib/observability` instead.
 */
import { clientLogger } from '@repo/observability/client'

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogContext {
  [key: string]: unknown
}

class LoggerService {
  private isDevelopment = process.env.NODE_ENV === 'development'
  private logLevel: LogLevel = this.isDevelopment ? 'info' : 'error'

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error']
    const currentLevelIndex = levels.indexOf(this.logLevel)
    const messageLevelIndex = levels.indexOf(level)
    return messageLevelIndex >= currentLevelIndex
  }

  /**
   * Set user context for all subsequent logs. Forwards to the shared
   * clientLogger so both this service and observability's window-level
   * handlers share the same userId / proId / userRole.
   */
  setUserContext(context: {
    userId?: string
    proId?: string
    userRole?: string
    userTimezone?: string
  }) {
    clientLogger.setUser({
      userId: context.userId,
      proId: context.proId,
      userRole: context.userRole,
    })
  }

  private formatConsole(level: LogLevel, message: string, context?: LogContext): void {
    if (!this.shouldLog(level)) return
    const timestamp = new Date().toISOString()
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`
    switch (level) {
      case 'debug':
        if (this.isDevelopment) console.log(prefix, message, context || '')
        break
      case 'info':
        console.info(prefix, message, context || '')
        break
      case 'warn':
        console.warn(prefix, message, context || '')
        break
      case 'error':
        if (context?.error) {
          const err = context.error as Error
          console.error(prefix, message, '\n', { message: err.message, stack: err.stack, ...context })
        } else {
          console.error(prefix, message, context || '')
        }
        break
    }
  }

  private extractContext(context?: LogContext) {
    if (!context) return { componentName: undefined, action: undefined, metadata: undefined }
    const { component, action, error: _err, ...rest } = context
    return {
      componentName: typeof component === 'string' ? component : undefined,
      action: typeof action === 'string' ? action : undefined,
      metadata: Object.keys(rest).length > 0 ? (rest as Record<string, unknown>) : undefined,
    }
  }

  debug(message: string, context?: LogContext): void {
    this.formatConsole('debug', message, context)
    // Debug is console-only by design — no DB write.
  }

  info(message: string, context?: LogContext): void {
    this.formatConsole('info', message, context)
    // Info is console-only by design — no DB write.
  }

  warn(message: string, context?: LogContext): void {
    this.formatConsole('warn', message, context)
    if (!this.isDevelopment) {
      const { componentName, action, metadata } = this.extractContext(context)
      void clientLogger.warn(new Error(message), {
        componentName,
        action,
        metadata,
        handled: true,
      })
    }
  }

  error(message: string, error?: Error | unknown, context?: LogContext): void {
    // Normalize error the same way the previous implementation did so
    // downstream metadata (code, statusCode, etc.) survives.
    let errorObj: Error
    if (error instanceof Error) {
      errorObj = error
    } else if (typeof error === 'object' && error !== null) {
      const errorInfo = error as Record<string, unknown>
      errorObj = new Error(
        typeof errorInfo.message === 'string' ? errorInfo.message : JSON.stringify(error),
      )
      if (errorInfo.code) (errorObj as unknown as { code?: unknown }).code = errorInfo.code
      if (errorInfo.status) (errorObj as unknown as { status?: unknown }).status = errorInfo.status
      if (errorInfo.statusCode)
        (errorObj as unknown as { statusCode?: unknown }).statusCode = errorInfo.statusCode
      ;(errorObj as unknown as { originalError?: unknown }).originalError = error
    } else if (error !== undefined) {
      errorObj = new Error(String(error))
    } else {
      // No error argument — treat message itself as the error string.
      errorObj = new Error(message)
    }

    this.formatConsole('error', message, { ...context, error: errorObj })

    const { componentName, action, metadata } = this.extractContext(context)
    const errorAny = errorObj as unknown as {
      code?: unknown
      status?: unknown
      statusCode?: unknown
      originalError?: unknown
    }
    void clientLogger.error(errorObj, {
      componentName,
      action,
      metadata: {
        ...(metadata || {}),
        ...(errorAny.code ? { errorCode: errorAny.code } : {}),
        ...(errorAny.status ? { errorStatus: errorAny.status } : {}),
        ...(errorAny.statusCode ? { errorStatusCode: errorAny.statusCode } : {}),
        ...(errorAny.originalError ? { originalError: errorAny.originalError } : {}),
        // Preserve the human-readable message from the call site — often
        // more descriptive than the underlying error's message.
        logMessage: message,
      },
      handled: true,
    })
  }

  // Perf helpers — dev-only console, no DB.
  time(label: string): void {
    if (this.isDevelopment) console.time(label)
  }

  timeEnd(label: string): void {
    if (this.isDevelopment) console.timeEnd(label)
  }
}

export const logger = new LoggerService()
export { LoggerService }
